<template>
  <section>
    <question v-if="display"></question>
    <div class="content-wrapper">
      <div class="content-heading">
        All users
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <breadcrumb class="breadcrumb" v-bind:breadcrumbsData="breadcrumbData" />
      <br />

      <div class="row">
        <spinner v-if="loading"></spinner>
        <div class="col-lg-12">
          <div id="panelChart9" class="panel panel-default">
            <div class="panel-title">Fail Transaction request</div>
            <div class="line-separator"></div>
            <div id="content-wrapper" class="bottom-margin">
              <div
                v-for="(field, idx) in fields()"
                :key="idx"
                class="row top-margin"
              >
                <div class="col-md-4">
                  <div>
                    <label>{{ field.text }}</label>
                  </div>
                </div>
                <div class="col-md-2">
                  <div>
                    <p class="text-right">
                      {{ getValue(field.accessor) }}
                    </p>
                  </div>
                </div>
              </div>
              <div id="comment-section" class="row top-margin">
                <div class="col-md-12">
                  <span>Please append your comment below *</span>
                  <restricted-textarea
                    :input="transaction.comment"
                    :limit="255"
                    id="comment"
                    :required="true"
                    placeholder=""
                    @inputUpdated="commentUpdated"
                  />
                  <p
                    class="top-margin"
                    style="color: red"
                    v-if="hasCommentChanged"
                  >
                    <i>You must enter or append a comment.</i>
                  </p>
                </div>
              </div>
              <div id="button-section" class="row top-margin">
                <div class="col-md-10">
                  <button
                    type="submit"
                    class="btn btn-info btn-lg button-white buttons"
                    v-on:click="handleCancelRequest"
                    v-bind:disabled="disableCancel"
                  >
                    Cancel request
                  </button>
                  <button
                    type="submit"
                    class="btn btn-info btn-lg button-white buttons"
                    v-on:click="approveFailTransaction"
                    v-bind:disabled="disableApprove"
                  >
                    Approve request
                  </button>
                  <button
                    type="submit"
                    class="btn btn-info btn-lg button-approve buttons"
                    v-on:click="submitRequest"
                    v-bind:disabled="disableSubmit"
                  >
                    Submit request
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import _ from 'lodash';

import RestrictedTextArea from '@/components/formControls/RestrictedTextArea';
import breadcrumb from '@/components/Breadcrumb';
import Question from '@/components/modals/Question';
import spinner from '@/components/widgets/Spinner';
import { getAccounTypeProperties, formatNegativeAmount } from '@/util';
import { UserTypes } from '@/data/static/userTypes';
import {
  SET_ERRORS_MODAL_INFO,
  SHOW_ERRORS_MODAL,
  HIDE_QUESTION_MODAL,
  SHOW_QUESTION_MODAL,
} from '@/store/mutation-types';

export default {
  name: 'FailTransaction',
  components: {
    spinner,
    breadcrumb,
    'restricted-textarea': RestrictedTextArea,
    question: Question,
  },
  mounted() {
    this.setParams();
    this.loadTransaction();
  },
  data() {
    return {
      name: '',
      userId: '',
      initialComment: '',
      noteId: '',
      transactionId: '',
      transaction: {
        comment: '',
        note: {
          account: { users: [] },
        },
      },
      breadcrumbData: [],
      loading: false,
    };
  },
  watch: {
    transaction: function onChangeTransaction(newTransaction) {
      const {
        accountName,
        accountType,
        id: accountId,
        users,
      } = newTransaction.note.account;
      this.breadcrumbData = [];
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: 'Search',
            goto: { name: 'Users' },
            selected: false,
          },
        ),
      );
      const params = Object.assign({}, this.$route.params);
      const user = users.find(search => search.id === Number(params.userId));
      const { userType, firstName, lastName } = user;
      let goto;
      if (userType === UserTypes.FINANCIAL_ADVISOR.name) {
        goto = {
          name: 'EditFinancialAdvisor',
          params: {
            name: params.name,
            userId: params.userId,
          },
        };
      } else {
        goto = {
          name: 'UserDetail',
          params: { name: params.name, userId: params.userId },
        };
      }
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            goto,
            title: firstName.concat(' ', lastName),
            selected: false,
          },
        ),
      );
      const accountTypeProperties = getAccounTypeProperties(accountType);
      const gotoAccount = {
        name: accountTypeProperties.component,
        params: {
          accountId,
          name: params.name,
          userId: params.userId,
        },
      };
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: accountName,
            goto: gotoAccount,
            selected: false,
          },
        ),
      );
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: 'Note'.concat(' ', newTransaction.note.noteId),
            goto: { name: 'NoteDetail', params: this.$route.params },
            selected: false,
          },
        ),
      );
      this.breadcrumbData.push(
        Object.assign(
          {},
          {
            title: `Fail Transaction ${newTransaction.transactionId}`,
            goto: { name: this.$route.name, params: this.$route.params },
            selected: true,
          },
        ),
      );
    },
  },
  computed: {
    ...mapState('question', [
      'display',
    ]),
    disableApprove() {
      return (
        this.transaction.hasPendingFailureApproval !== true ||
        this.isButtonDisabled
      );
    },
    disableSubmit() {
      return this.hasCommentChanged || this.transaction.hasPendingFailureApproval === true;
    },
    disableCancel() {
      return (
        (this.transaction.hasPendingFailureApproval === null ||
          this.transaction.hasPendingFailureApproval === true) &&
        this.isButtonDisabled
      );
    },
    isButtonDisabled() {
      return this.hasCommentChanged;
    },
    hasCommentChanged() {
      return (
        !this.transaction.comment ||
        this.initialComment === this.transaction.comment
      );
    },
  },
  methods: {
    ...mapActions('transaction', ['getTransaction', 'markTransactionAsFailed']),
    ...mapMutations('question', {
      hideQuestionModal: HIDE_QUESTION_MODAL,
      showQuestionModal: SHOW_QUESTION_MODAL,
    }),
    ...mapMutations('errors', {
      showErrorsModal: SHOW_ERRORS_MODAL,
      setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
    }),
    fields() {
      return [
        {
          text: 'Note Id',
          accessor: 'note.noteId',
        },
        {
          text: 'Transaction Id',
          accessor: 'transactionId',
        },
        {
          text: 'Amount',
          accessor: 'amount',
        },
      ];
    },
    getValue(accessor = '') {
      return _.get(this.transaction, accessor);
    },
    commentUpdated($event) {
      this.transaction.comment = $event;
    },
    loadTransaction() {
      this.loading = true;
      this.getTransaction(this.transactionId).then((transaction) => {
        this.transaction = transaction;
        this.initialComment = transaction.comment;
        this.transaction.comment = transaction.comment || '';
        this.transaction.amount = formatNegativeAmount(transaction.amount);
        this.loading = false;
      });
    },
    setParams() {
      this.name = this.$route.params.name;
      this.userId = this.$route.params.userId;
      this.noteId = this.$route.params.noteId;
      this.transactionId = this.$route.params.transactionId;
    },
    goBack() {
      this.$router.go(-1);
    },
    showConfirmationModal() {
      this.showQuestionModal({
        title: 'Cancel Request to Mark transaction as failed',
        question: `Are you sure you want to cancel the request to mark T${this.transaction.id} as failed?`,
        method: () => {
          this.submitRequest(true);
        },
        yesButtonText: 'Yes',
        hideCancelButton: false,
      });
    },
    handleCancelRequest() {
      if (this.transaction.hasPendingFailureApproval === null) {
        this.goBack();
      } else {
        this.showConfirmationModal();
      }
    },
    submitRequest(cancel = false) {
      const { id, comment } = this.transaction;
      this.markTransactionAsFailed({ id, comment, cancel })
        .then(() => {
          this.hideQuestionModal();
          this.$router.push({
            name: 'NoteDetail',
            params: {
              name: this.name,
              noteId: this.noteId,
              userId: this.userId,
            },
          });
        })
        .catch(() => {
          const errors = [];
          if (this.transaction.hasPendingFailureApproval === null) {
            errors.push(
              `Something went wrong while trying to request marking T${id} as failed`,
            );
          } else {
            errors.push(
              `Something went wrong while trying to approve mark T${id} as failed`,
            );
          }
          this.setErrorsModalInfo(errors);
          this.showErrorsModal();
        });
    },
    approveFailTransaction() {
      this.submitRequest();
    },
  },
};
</script>

<style scoped>
.line-separator {
  padding: 8px;
  line-height: 1.52857143;
  vertical-align: top;
  border-top: 1px solid #eee;
}
.line-separator2 {
  line-height: 2.52857143;
  vertical-align: top;
  width: 49%;
  border-top: 1px solid black;
}
.left-margin {
  margin-left: 2%;
}
.right-margin {
  margin-left: -4%;
}
.top-margin {
  margin-top: 2%;
}
.bottom-margin {
  margin-bottom: 1%;
}
.space {
  margin-top: 4%;
}
.content-heading {
  font-weight: normal;
}
.panel-title {
  font-size: 20px;
}
.panel-subtitle {
  font-size: 16px;
}
.panel-default {
  padding: 30px 40px;
  width: 100%;
}
.panel-heading {
  background-color: #ffffff;
  font-size: 16px;
  padding: 0px 5px;
}
label {
  color: #888;
  font-size: 14px;
  width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.total-amount {
  font-size: 20px;
}
.button-approve {
  background-color: #127285;
}
.button-white {
  background-color: white;
  border-color: #127285;
  color: #127285;
}
.btn-back {
  color: #31b0d5;
  background-color: Transparent;
  background-repeat: no-repeat;
  border: none;
  cursor: pointer;
  overflow: hidden;
  outline: none;
  padding-left: 20px;
}
.icon-back {
  position: relative;
}
.icon-back:before {
  content: '\f060';
  font-family: FontAwesome;
  position: absolute;
  left: 0px;
}
.radio-item {
  display: inline-block;
  position: relative;
  padding: 0 6px;
}
.radio-item input[type='radio'] {
  display: none;
}
.radio-item label {
  color: #666;
  font-weight: normal;
  cursor: pointer;
}
.radio-item label:before {
  content: ' ';
  display: inline-block;
  position: relative;
  top: 0px;
  margin: 0 5px 0 0;
  width: 20px;
  height: 20px;
  border-radius: 11px;
  background-color: transparent;
}
.radio-item-unselected label:before {
  border: 2px solid #d3d3d3;
}
.radio-item input[type='radio']:checked + label:after {
  border-radius: 11px;
  width: 10px;
  height: 10px;
  position: absolute;
  top: 5px;
  left: 11px;
  content: ' ';
  display: block;
}
.buttons {
  display: inline-block;
  margin: 5px;
}
</style>



// WEBPACK FOOTER //
// FailTransaction.vue?15799e5b